import React from "react";
import { Navigate } from "react-router-dom";
import { UnauthorizedPage } from "../../app/router/UnauthorizedPage";
import { RequireSuperAdmin } from "./components/RequireSuperAdmin";
import { SuperAdminLayout } from "./components/SuperAdminLayout";
import { AcademiesPage } from "./pages/AcademiesPage";
import { InvoicesPage } from "./pages/InvoicesPage";
import { MetricsPage } from "./pages/MetricsPage";
import { SubscriptionsPage } from "./pages/SubscriptionsPage";
import { AuditLogPage } from "./staff/pages/AuditLogPage";
import { StaffPage } from "./staff/pages/StaffPage";

/**
 * Superadmin feature routes — TASK-18 (R6: owned exclusively by src/features/superadmin/).
 *
 * All routes are protected by RequireSuperAdmin (verifica is_staff || role='SUPERADMIN').
 * TASK-22 añade `requiredRoles` para las rutas que el backend restringe a un rol
 * concreto de staff, y filtra pestañas/controles con `useStaffPermissions`.
 *
 *   /superadmin/academias       → AcademiesPage     — listar/alta/suspender academias
 *   /superadmin/suscripciones   → SubscriptionsPage — gestionar plan/status/trial
 *   /superadmin/facturas        → InvoicesPage      — facturas con IVA 19%
 *   /superadmin/metricas        → MetricsPage       — KPIs + gráficas recharts
 *   /superadmin/staff           → StaffPage         — equipo interno (PLATFORM_ADMIN)
 *   /superadmin/bitacora        → AuditLogPage      — auditoría (PLATFORM_ADMIN, READ_ONLY)
 *
 * Register in AppRouter.js:
 *   import superadminRoutes from '../../features/superadmin/routes';
 *   registry.register(superadminRoutes);
 */
const superadminRoutes = [
  {
    path: "/superadmin",
    element: (
      <RequireSuperAdmin>
        <SuperAdminLayout />
      </RequireSuperAdmin>
    ),
    children: [
      // Sin esto, /superadmin a secas renderiza el layout con el outlet vacío.
      { index: true, element: <Navigate to="academias" replace /> },
      { path: "academias", element: <AcademiesPage /> },
      { path: "suscripciones", element: <SubscriptionsPage /> },
      { path: "facturas", element: <InvoicesPage /> },
      { path: "metricas", element: <MetricsPage /> },
      // superadmin/staff/ es PLATFORM_ADMIN-only en el backend (403 en todos los
      // métodos para el resto de roles, incluido GET).
      {
        path: "staff",
        element: (
          <RequireSuperAdmin requiredRoles={["PLATFORM_ADMIN"]}>
            <StaffPage />
          </RequireSuperAdmin>
        ),
      },
      // superadmin/audit/ lo pueden leer PLATFORM_ADMIN y READ_ONLY.
      {
        path: "bitacora",
        element: (
          <RequireSuperAdmin requiredRoles={["PLATFORM_ADMIN", "READ_ONLY"]}>
            <AuditLogPage />
          </RequireSuperAdmin>
        ),
      },
    ],
  },
  // Destino de los redirects de RequireRole y RequireSuperAdmin. No estaba
  // registrado en ningún routes.js, así que un acceso denegado dejaba la
  // pantalla en blanco. `public: true` la renderiza sin el shell de tenant.
  { path: "/unauthorized", element: <UnauthorizedPage />, public: true },
];

export default superadminRoutes;
